import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {FilterTransform} from 'lab-automation/settings/RunSchemaSpecConstants';
import {
  FilterType,
  FILTER_TYPE_TO_READABLE,
  NUMERIC_FILTER_TYPES,
} from 'lookup-config/LookupConfigModalConstants';
import {Schema} from 'schemas/SchemaConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {GRAY_DARKER} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import ErrorMessage from 'ui-library/ErrorMessage';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import NumericInput from 'ui-library/inputs/NumericInput';
import TextField from 'ui-library/inputs/TextField';
import TextInput from 'ui-library/inputs/TextInput';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {InputChangeEvent, NumericInputChangeEvent} from 'util/Types';

import LookupConfigInput from './LookupConfigInput';
import {
  CSVTextAreaInput,
  FilterFormErrors,
  getColumnNamesFromInput,
  MULTI_VALUE_FILTER_TYPES,
  SINGLE_VALUE_FILTER_TYPES,
  updateTransformationProperties,
  validateLookupIsNumeric,
} from './TransformFormUtils';

interface FilterValuesInputProps {
  baseSchema: Schema;
  transform: FilterTransform;
  onChange: (transform: FilterTransform) => void;
  formErrors: FilterFormErrors;
  type: FilterValuesType;
  onChangeFilterValuesType: (type: FilterValuesType) => void;
}

export enum FilterValuesType {
  LOOKUP = 'Lookup',
  LIST = 'List of values',
  VALUE = 'Value',
}

function getInitialType(transform: FilterTransform): FilterValuesType {
  if (transform.lookupConfig) {
    return FilterValuesType.LOOKUP;
  }
  return MULTI_VALUE_FILTER_TYPES.includes(transform.transformationProperties.type!)
    ? FilterValuesType.LIST
    : FilterValuesType.VALUE;
}

export function FilterValuesInput(props: FilterValuesInputProps): JSX.Element | null {
  const filterType = props.transform.transformationProperties?.type;
  if (!filterType || ![...MULTI_VALUE_FILTER_TYPES, ...SINGLE_VALUE_FILTER_TYPES].includes(filterType)) {
    return null;
  }
  const isMulti = MULTI_VALUE_FILTER_TYPES.includes(filterType);
  const isNumeric = NUMERIC_FILTER_TYPES.includes(filterType);
  const type = props.type;
  return (
    <Row className={css(styles.multipleValuesInputContainer)}>
      <Col md={4}>
        <FieldLabel label="Input type" labelStyle={LabelStyle.LABEL} isRequired={true} />
        <SelectDropdown
          alwaysDropDown={true}
          displayName={type}
          menu={
            <Menu value={type} height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.SMALL} scrollable={true}>
              {isMulti ? (
                <Option
                  value={FilterValuesType.LIST}
                  onSelect={() => {
                    const newTransformationProperties = {...props.transform.transformationProperties};
                    delete newTransformationProperties.value;
                    const newTransform = {
                      ...props.transform,
                      transformationProperties: newTransformationProperties,
                    };
                    delete newTransform.lookupConfig;
                    props.onChange(newTransform);
                    props.onChangeFilterValuesType(FilterValuesType.LIST);
                  }}
                >
                  {FilterValuesType.LIST}
                </Option>
              ) : null}
              {!isMulti ? (
                <Option
                  value={FilterValuesType.VALUE}
                  onSelect={() => {
                    const newTransformationProperties = {...props.transform.transformationProperties};
                    delete newTransformationProperties.values;
                    const newTransform = {
                      ...props.transform,
                      transformationProperties: newTransformationProperties,
                    };
                    delete newTransform.lookupConfig;
                    props.onChange(newTransform);
                    props.onChangeFilterValuesType(FilterValuesType.VALUE);
                  }}
                >
                  {FilterValuesType.VALUE}
                </Option>
              ) : null}
              <Option
                value={FilterValuesType.LOOKUP}
                onSelect={() => {
                  const newTransformationProperties = {...props.transform.transformationProperties};
                  delete newTransformationProperties.values;
                  delete newTransformationProperties.value;
                  props.onChange({...props.transform, transformationProperties: newTransformationProperties});
                  props.onChangeFilterValuesType(FilterValuesType.LOOKUP);
                }}
              >
                {FilterValuesType.LOOKUP}
              </Option>
            </Menu>
          }
        />
      </Col>
      <Col md={8}>
        <FieldLabel label={type} labelStyle={LabelStyle.LABEL} isRequired={true} />
        {(() => {
          switch (type) {
            case FilterValuesType.LOOKUP:
              return (
                <>
                  <LookupConfigInput
                    baseSchema={props.baseSchema}
                    transform={props.transform}
                    onChange={props.onChange}
                    defaultIsMultiValue={isMulti}
                    shouldShowIsMulti={false}
                    getLookupConfigValidationError={isNumeric ? validateLookupIsNumeric : undefined}
                  />
                  {props.formErrors.lookupConfig ? <ErrorMessage error="Lookup is required" /> : null}
                </>
              );
            case FilterValuesType.LIST:
              return (
                <>
                  <CSVTextAreaInput
                    value={props.transform.transformationProperties?.values}
                    onChange={(values) => {
                      props.onChange(updateTransformationProperties(props.transform, 'values', values));
                    }}
                    parseCSVInputFn={getColumnNamesFromInput}
                    hasError={props.formErrors.values}
                  />
                  {props.formErrors.values ? <ErrorMessage error="Values are required" /> : null}
                  <div className={css(styles.description)}>Enter a list of values, separated by comma.</div>
                </>
              );
            case FilterValuesType.VALUE:
              return (
                <FilterValueField
                  isNumeric={isNumeric}
                  value={props.transform.transformationProperties?.value}
                  onChange={(value: string | number) => {
                    props.onChange(updateTransformationProperties(props.transform, 'value', value));
                  }}
                  hasError={props.formErrors.value}
                />
              );
            default:
              return null;
          }
        })()}
      </Col>
    </Row>
  );
}

interface FilterValueFieldProps {
  isNumeric: boolean;
  value: string | number | undefined;
  onChange: (value: string | number) => void;
  hasError: boolean;
}

export function FilterValueField(props: FilterValueFieldProps): JSX.Element | null {
  if (props.isNumeric) {
    return (
      <>
        <NumericInput
          value={props.value as number | undefined}
          onChange={(e: NumericInputChangeEvent) => {
            if (e.target.numericValue != null) {
              props.onChange(e.target.numericValue);
            }
          }}
        />
        {props.hasError ? <ErrorMessage error="Value is required" /> : null}
      </>
    );
  }
  return (
    <>
      <TextInput
        value={props.value}
        onChange={(e: InputChangeEvent) => {
          props.onChange(e.target.value);
        }}
        hasError={props.hasError}
      />
      {props.hasError ? <ErrorMessage error="Value is required" /> : null}
    </>
  );
}

interface FilterFormProps {
  baseSchema: Schema;
  transform: FilterTransform;
  onChange: (transform: FilterTransform) => void;
  formErrors: FilterFormErrors;
}

export default function FilterForm(props: FilterFormProps): JSX.Element {
  const [filterValuesType, setFilterValuesType] = useState(getInitialType(props.transform));
  return (
    <>
      <Row>
        <Col md={4}>
          <TextField
            label="Column name"
            onChange={(e) => {
              props.onChange(updateTransformationProperties(props.transform, 'column', e.target.value));
            }}
            labelStyle={FieldLabel.STYLES.LABEL}
            isRequired={true}
            value={props.transform.transformationProperties?.column}
            hasError={props.formErrors.column}
          />
          {props.formErrors.column ? <ErrorMessage error="Column name is required" /> : null}
        </Col>
        <Col md={4}>
          <FieldLabel label="Filter type" labelStyle={LabelStyle.LABEL} isRequired={true} />
          <SelectDropdown
            alwaysDropDown={true}
            displayName={
              props.transform.transformationProperties?.type
                ? FILTER_TYPE_TO_READABLE[props.transform.transformationProperties?.type]
                : 'Select'
            }
            error={props.formErrors.type ? 'Filter type is required' : null}
            menu={
              <Menu
                value={props.transform.transformationProperties?.type}
                height={Menu.HEIGHTS.MEDIUM}
                width={Menu.WIDTHS.SMALL}
                scrollable={true}
              >
                {Object.values(FilterType).map((filterType, i) => (
                  <Option
                    key={i}
                    value={filterType}
                    onSelect={(filterType: FilterType) => {
                      const newTransformationProperties = {
                        ...props.transform.transformationProperties,
                        type: filterType,
                      };
                      if (
                        !SINGLE_VALUE_FILTER_TYPES.includes(filterType) ||
                        (NUMERIC_FILTER_TYPES.includes(filterType) &&
                          typeof newTransformationProperties.value === 'string')
                      ) {
                        delete newTransformationProperties.value;
                      }
                      if (
                        !NUMERIC_FILTER_TYPES.includes(filterType) &&
                        typeof newTransformationProperties.value === 'number'
                      ) {
                        newTransformationProperties.value = newTransformationProperties.value.toString();
                      }
                      if (!MULTI_VALUE_FILTER_TYPES.includes(filterType)) {
                        delete newTransformationProperties.values;
                      }
                      const newTransform = {
                        ...props.transform,
                        transformationProperties: newTransformationProperties,
                      };
                      if (![...MULTI_VALUE_FILTER_TYPES, ...SINGLE_VALUE_FILTER_TYPES].includes(filterType)) {
                        delete newTransform.lookupConfig;
                      }
                      props.onChange(newTransform);
                      setFilterValuesType((prevType) => {
                        if (
                          prevType === FilterValuesType.LIST &&
                          SINGLE_VALUE_FILTER_TYPES.includes(filterType)
                        ) {
                          return FilterValuesType.VALUE;
                        }
                        if (
                          prevType === FilterValuesType.VALUE &&
                          MULTI_VALUE_FILTER_TYPES.includes(filterType)
                        ) {
                          return FilterValuesType.LIST;
                        }
                        return prevType;
                      });
                    }}
                  >
                    {FILTER_TYPE_TO_READABLE[filterType]}
                  </Option>
                ))}
              </Menu>
            }
          />
        </Col>
      </Row>
      <FilterValuesInput
        {...props}
        type={filterValuesType}
        onChangeFilterValuesType={(type) => {
          setFilterValuesType(type);
        }}
      />
    </>
  );
}
const styles = StyleSheet.create({
  description: {
    fontSize: 13,
    lineHeight: '16px',
    color: GRAY_DARKER,
    paddingTop: '7px',
  },
  multipleValuesInputContainer: {
    paddingTop: '11px',
  },
});
